import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useNavigate } from 'react-router-dom';
import { BookOpenText, ContactRound, FolderKanban, LogOut, Network } from 'lucide-react';
import type { ComponentType } from 'react';
import { ROUTES } from '@/app/router/routes';
import { useAuth } from '@/app/providers/AuthProvider';
import type { Permission } from '@dt-pm/core';
import { initials, avatarColor } from '@/shared/lib/mock-data';
import { cn } from '@/shared/lib/cn';
import { SidebarLogo } from './SidebarLogo';
import { SidebarTimer } from './SidebarTimer';
import { ThemeMenuButton } from './ThemeMenuButton';

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number }>;
  permission?: Permission;
}

const NAV_ITEMS: NavItem[] = [
  { to: ROUTES.projects, label: 'Projetos', icon: FolderKanban },
  { to: ROUTES.adminUsers, label: 'Gestão de usuários', icon: ContactRound, permission: 'admin.users.manage' },
  { to: ROUTES.adminSpaces, label: 'Estrutura', icon: Network, permission: 'admin.spaces.manage' },
  { to: ROUTES.portfolio, label: 'Portfólio', icon: BookOpenText, permission: 'admin.panel.view' },
];

/**
 * Linha das tabs de /projetos, a partir do topo da viewport:
 * padding da página (16) + título (40) + espaço (12) + faixa das tabs (42).
 */
const TAB_RULE_OFFSET_PX = 16 + 40 + 12 + 42;

/** Navegação principal compacta, conforme a barra lateral global do Figma. */
export function Sidebar() {
  const { user, hasPermission } = useAuth();
  const visibleNavItems = NAV_ITEMS.filter((item) => !item.permission || hasPermission(item.permission));

  function MenuItem({ item }: { item: NavItem }) {
    const { to, label, icon: Icon } = item;

    return (
      <NavLink
        to={to}
        title={label}
        className={({ isActive }) =>
          cn(
            'app-sidebar-link group flex w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-lg',
            isActive && 'is-active',
          )
        }
      >
        {({ isActive }) => (
          <>
            <span
              data-active={isActive ? 'true' : 'false'}
              className={cn(
                'app-sidebar-item flex h-8 w-8 items-center justify-center rounded-xl transition-colors duration-150',
                isActive ? 'brand-fill bg-sidebar-icon-active text-white' : 'text-white hover:bg-white/10',
              )}
            >
              <Icon size={20} />
            </span>
            <span className="w-full px-0.5 text-center text-[10px] leading-tight font-semibold text-white">
              {label}
            </span>
          </>
        )}
      </NavLink>
    );
  }

  return (
    <aside className="app-sidebar flex w-[72px] shrink-0 flex-col items-center overflow-visible bg-sidebar px-3 pb-4 select-none">
      <div
        className="flex w-full shrink-0 items-start justify-center pt-9"
        style={{ height: TAB_RULE_OFFSET_PX }}
      >
        <SidebarLogo />
      </div>
      <div aria-hidden className="h-px w-4/5 shrink-0 bg-white/10" />

      <nav
        aria-label="Navegação principal"
        className="scrollbar-none mt-4 flex min-h-0 w-full flex-1 flex-col items-center gap-2 overflow-y-auto overflow-x-hidden"
      >
        {visibleNavItems.map((item) => (
          <MenuItem key={item.label} item={item} />
        ))}
      </nav>

      <div className="mt-auto flex flex-col items-center gap-4 pt-4">
        <SidebarTimer />
        <ThemeMenuButton className="text-white hover:bg-white/10" />
        {user ? <SidebarUserMenu name={user.name} avatarUrl={user.avatarUrl} userId={user.id} /> : null}
      </div>
    </aside>
  );
}

function SidebarUserMenu({
  name,
  avatarUrl,
  userId,
}: {
  name: string;
  avatarUrl?: string | null;
  userId: string;
}) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 84 });

  const placeMenu = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight ?? 48;
    const left = rect.right + 12;
    const top = Math.max(16, rect.bottom - menuHeight);
    setPos((current) => (current.top === top && current.left === left ? current : { top, left }));
  }, []);

  useEffect(() => {
    if (!open) return;
    placeMenu();
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', placeMenu);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', placeMenu);
    };
  }, [open, placeMenu]);

  async function handleLogout(): Promise<void> {
    setOpen(false);
    await logout();
    navigate(ROUTES.login, { replace: true });
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        title={name}
        aria-label={`Conta de ${name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setOpen((current) => !current);
        }}
        className={cn(
          'flex h-10 w-10 cursor-pointer items-center justify-center overflow-hidden rounded-full text-xs font-semibold text-white',
          !avatarUrl && avatarColor(userId),
        )}
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={name}
            referrerPolicy="no-referrer"
            className="h-full w-full rounded-full object-cover"
            onError={(event) => {
              event.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          initials(name)
        )}
      </button>

      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              aria-label="Conta"
              style={{ top: pos.top, left: pos.left }}
              className="fixed z-50 min-w-[160px] rounded-xl border border-line bg-surface p-1 shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-1px_rgba(0,0,0,0.06)]"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                role="menuitem"
                className="flex h-10 w-full cursor-pointer items-center gap-2 rounded-lg px-3 text-sm font-semibold text-ink hover:bg-canvas"
                onClick={() => void handleLogout()}
              >
                <LogOut size={16} className="text-ink-muted" />
                Sair
              </button>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
